import { useCategoryPageFilterKeys } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
import { useCategoryPageQueryKeys } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageQueryKeys";
import { useIsCategoryPageError } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageError";
import { useIsCategoryPageLoadedAndHasNoResults } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageLoadedAndHasNoResults";
import {
  useCategoryPageDataUpdatedAt,
  useIsCategoryPageLoading,
} from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageLoading";
import { useIsLayoutElementsError } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useIsLayoutElementsError";
import { useIsTopOfPageLayoutElementsLoadedAndHasNoResults } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useIsTopOfPageLayoutElementsLoadedAndHasNoResults";
import {
  useLayoutElementsDataUpdatedAt,
  useLayoutElementsLoading,
} from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useLayoutElementsLoading";
import { useLayoutElementsQueryKey } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useLayoutElementsQueryKey";
import { useCategoryPageActionName } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useCategoryPageActionName";
import { useIsHubCategoryLoadingStrategy } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsHubCategoryLoadingStrategy";
import { useOneIndexedPageNumber } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useOneIndexedPageNumber";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import isEqual from "lodash/isEqual";
import { useMemo, useRef } from "react";

enum PageLoadVariant {
  FILTER_UPDATE = "FILTER_UPDATE",
  PAGE_NUMBER_UPDATE = "PAGE_NUMBER_UPDATE",
}

const usePageLoadVariant = (): PageLoadVariant | undefined => {
  const filterKeys = useCategoryPageFilterKeys();
  const pageNumber = useOneIndexedPageNumber();

  const previousFiltersRef = useRef(filterKeys);
  const pageNumberRef = useRef(pageNumber);

  const variant = useMemo(() => {
    if (!isEqual(previousFiltersRef.current, filterKeys)) {
      // filter keys changed -- page number is automatically reset to 1
      previousFiltersRef.current = filterKeys;
      pageNumberRef.current = 1;
      return PageLoadVariant.FILTER_UPDATE;
    } else if (pageNumberRef.current !== pageNumber) {
      // page number changed
      pageNumberRef.current = pageNumber;
      return PageLoadVariant.PAGE_NUMBER_UPDATE;
    }

    return undefined;
  }, [filterKeys, pageNumber]);

  return variant;
};

const useIsPageLoading = (): { isLoading: boolean; dataUpdatedAt: number } => {
  const isCatNavLoading = useIsCategoryPageLoading();
  const isLayoutElementsLoading = useLayoutElementsLoading();

  const isCatNavError = useIsCategoryPageError();
  const isLayoutElementsError = useIsLayoutElementsError();

  const catNavDataUpdatedAt = useCategoryPageDataUpdatedAt();
  const layoutElementsDataUpdatedAt = useLayoutElementsDataUpdatedAt();

  const isUsingHubLoadingStrategy = useIsHubCategoryLoadingStrategy();

  return isUsingHubLoadingStrategy
    ? {
        isLoading: isLayoutElementsLoading && !isLayoutElementsError,
        dataUpdatedAt: layoutElementsDataUpdatedAt,
      }
    : {
        isLoading: isCatNavLoading && !isCatNavError,
        dataUpdatedAt: catNavDataUpdatedAt,
      };
};

const useIgnoreImages = (): boolean => {
  const isCatNavLoadedAndHasNoResults =
    useIsCategoryPageLoadedAndHasNoResults();
  const isCatNavError = useIsCategoryPageError();

  const isLayoutElementsError = useIsLayoutElementsError();
  const isLayoutElementsLoadedAndHasNoResults =
    useIsTopOfPageLayoutElementsLoadedAndHasNoResults();

  const isUsingHubLoadingStrategy = useIsHubCategoryLoadingStrategy();

  return isUsingHubLoadingStrategy
    ? isLayoutElementsLoadedAndHasNoResults || isLayoutElementsError
    : isCatNavLoadedAndHasNoResults || isCatNavError;
};

const usePageLoadKey = (): string => {
  const catNavqQueryKey = useCategoryPageQueryKeys();
  const layoutElementsQueryKey = useLayoutElementsQueryKey();

  const isUsingHubLoadingStrategy = useIsHubCategoryLoadingStrategy();

  return isUsingHubLoadingStrategy
    ? JSON.stringify(layoutElementsQueryKey)
    : JSON.stringify(catNavqQueryKey);
};

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const useLoggedInCategoryPageUPLT = () => {
  const isUsingHubLoadingStrategy = useIsHubCategoryLoadingStrategy();

  const pageName = useCategoryPageActionName();
  const { isLoading, dataUpdatedAt } = useIsPageLoading();
  const ignoreImages = useIgnoreImages();
  const variant = usePageLoadVariant();
  const pageLoadKey = usePageLoadKey();

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: pageName,
    isPageLoading: isLoading,
    ignoreImages: ignoreImages,
    enableTrackPerformanceBreakdown: "internal-tracking",
    variant: isUsingHubLoadingStrategy ? undefined : variant,
    pageLoadKey: pageLoadKey,
    numComponents: isUsingHubLoadingStrategy ? 3 : 1,
    dataUpdatedAt: dataUpdatedAt,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
  });

  return loadTimingRef;
};
